import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  DrawerBody,
  DrawerFooter,
  TextField,
  FormController,
  FormLabel,
  Button,
  ButtonGroup
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { TRACK, ERROR, SCREENS } from '../../AddToConstants';
import { AddToListProductInfo } from './AddToListProductInfo';
import { useCreateList, createListModel } from '../../hooks/useLists';
import { AddToCoreContext } from '../../core/AddToCoreContext';

export const CreateList = ({
  onClose,
  onCreate,
  products,
  isExistingLists,
  isSharedList,
  setFavoriteCount,
  favoriteCount,
  productValue,
  identifiers,
  primaryImage,
  setNewListDetails,
  isItemBundle,
  bundledProducts
}) => {
  const { setScreen, setSelectedListId, setSelectedListName } = useContext(AddToCoreContext);
  const { customerType } = useThdCustomer() || {};
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const { LIST_SUCCESS } = SCREENS;
  const isMultiAdd = products?.length > 1;
  const singleProduct = [
    { itemId: products[0]?.itemId, quantity: products[0]?.quantity }
  ];

  const multipleProduct = products.map((prod) => ({
    itemId: prod.itemId,
    quantity: prod.quantity
  }));

  const getProductsForList = () => {
    if (isItemBundle) {
      return bundledProducts;
    }
    return isMultiAdd ? multipleProduct.reverse() : singleProduct;
  };

  const { createList, createListResponse } = useCreateList({
    products: getProductsForList()
  });

  const getValidationError = (value) => {
    const validCharacters = /^[ [\]{}|a-z|A-Z|!@#$%^&*()0-9-_+,.?"':;\\]+$/;
    const isBlank = value.length === 0;
    let isValid = validCharacters.test(value);
    if (!isValid || isBlank) {
      if (isBlank) {
        setError(ERROR.EMPTY_LIST);
      } else {
        setError(ERROR.INVALID_LIST_NAME);
      }
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (!error) {
      await createList({
        variables: {
          name
        }
      });
    }
  };

  useEffect(() => {
    if (!createListResponse.error && createListResponse?.data?.createList?.name) {
      // Check if this is a single product add (not multi-add or shared list)
      const isSingleProductAdd = !isMultiAdd && products[0]?.itemId && !isSharedList;

      // Determine creation location based on context using a priority map
      const creationLocationMap = [
        [isSharedList, 'save shared list'],
        [isSingleProductAdd && customerType === 'B2C', 'save to your list overlay'],
        [isExistingLists, 'choose a list overlay'],
        [true, 'create a list overlay'] // default case
      ];

      const creationLocation = creationLocationMap.find(([condition]) => condition)?.[1];

      // Only trigger analytics if we have products
      if (products?.length > 0) {
        const allSkus = new Set();
        let listTotal = 0;
        let quantityTotal = 0;

        const items = products.map((prod) => {
          const quantity = prod.quantity || 1;
          const price = !isMultiAdd ? (productValue || 0) : (prod.price || 0);
          const sku = prod.itemId || 'n/a';

          // Accumulate metrics while mapping
          if (sku !== 'n/a') allSkus.add(sku);
          quantityTotal += quantity;
          listTotal += (price * quantity);

          return {
            quantity,
            price,
            sku
          };
        });

        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.NEW_LIST_CREATED_WITH_PRODUCT_ADD, {
          location: creationLocation,
          items,
          listTotal: Math.round(listTotal * 100) / 100,
          uniqueSkuCount: allSkus.size,
          quantityTotal
        });
      }
      onCreate();
    } else if (createListResponse?.error) {
      const gqlErrors = createListResponse?.error?.graphQLErrors;
      gqlErrors.forEach(function (err, index) {
        if (err?.message === ERROR.LIST_ALREADY_EXISTS) {
          setError(ERROR.LIST_ALREADY_EXISTS);
        } else {
          setError(ERROR.GENERAL_API_FAIL);
        }
      });
    }
  }, [createListResponse?.data, createListResponse?.error]);

  const newList = createListResponse?.data?.createList;

  useEffect(() => {
    if (newList?.id) {
      setFavoriteCount(favoriteCount + 1);
      setNewListDetails({
        listName: newList.name,
        listId: newList.id
      });
      setSelectedListId(newList.id);
      setSelectedListName(newList.name);
      setScreen(LIST_SUCCESS);
    }
  }, [newList?.id]);

  return (
    <>
      <DrawerBody>
        <AddToListProductInfo
          identifiers={identifiers}
          primaryImage={primaryImage}
          intent={products[0]?.intent}
        />
        <FormController className="sui-px-6 sui-pt-6">
          <FormLabel id="create-list" className="sui-cursor-default sui-text-base">
            Create List Name:
          </FormLabel>
          <div className="sui-pt-2">
            <TextField
              fullWidth
              placeholder="Enter a List Name"
              value={name}
              onChange={(evt) => {
                setName(evt.target.value);
                getValidationError(evt.target.value);
              }}
              status={error ? 'error' : null}
              statusMessage={error}
            />
          </div>
        </FormController>
      </DrawerBody>
      <DrawerFooter position="center">
        <div className="sui-flex sui-flex-col sui-gap-2">
          <ButtonGroup orientation="vertical">
            <Button
              data-testid="create-group-submit-button"
              fullWidth
              variant="primary"
              onClick={handleSubmit}
              type="submit"
              disabled={!name || !!error}
            >
              Save
            </Button>
            <Button
              data-testid="create-group-cancel-button"
              fullWidth
              variant="secondary"
              onClick={() => { onClose('close', { isUserInitiated: true }); }}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </div>
      </DrawerFooter>
    </>
  );
};

CreateList.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  isExistingLists: PropTypes.bool.isRequired,
  isSharedList: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  setFavoriteCount: PropTypes.func.isRequired,
  favoriteCount: PropTypes.number,
  setNewListDetails: PropTypes.func,
  productValue: PropTypes.number,
  identifiers: PropTypes.shape({
    brandName: PropTypes.string,
    productLabel: PropTypes.string
  }),
  primaryImage: PropTypes.shape({
    url: PropTypes.string
  }),
  isItemBundle: PropTypes.bool,
  bundledProducts: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  }))
};

CreateList.defaultProps = {
  products: [],
  favoriteCount: null,
  productValue: 0,
  identifiers: {},
  primaryImage: {},
  setNewListDetails: () => { },
  isItemBundle: false,
  bundledProducts: []
};

CreateList.displayName = 'CreateList';

CreateList.dataModel = extend(createListModel);
